import { Dialog, DialogContent, DialogTitle, Grid, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { AutomationRulesLabel, emptyLabels, GridStatusLabelVariant, pageTitles, Tags } from '../shared/constants';
import { AutoAlertRulesService } from '../../services/AutoAlertRulesService';
import { AutoAlertLogsModel, mapToAutoAlertLogs, RuleModel, RuleType } from '../../models/auto-alert.model';
import { Contact } from '../../models/conversations';
import { UtilityService } from '../../services/UtilityService';
import { setHeaderCount, setHeaderTitle, setRefreshHandler, setSelectedCount } from '../../helpers/header.helper';
import ContactInformation from "../shared/contact-details.component";
import ContactView from "../contacts/contact-view.component";
import GridComponent from '../shared/grid';
import { ColDef, ValueGetterParams } from 'ag-grid-community';
import { StatusRenderer } from '../shared/grid/statusRenderer';
import { dateComparator } from '../shared/grid/grid.services';
import { CancelIcon, MediaDefaultIcon } from '../../icon';
import Mention from '../shared/mentions';
import { useSelector } from 'react-redux';
import * as selector from "../../selectors/selectors"

interface IProps {
    GridApi: any;
    OnGridReady: (params: any) => void;

}

const LogsComponent = (props: IProps) => {
    const IsMobileDevice: boolean = useSelector(selector.isMobileDevice)
    const Contacts: Contact[] = useSelector(selector.contacts)
    const PrivateRules: RuleModel[] = useSelector(selector.privateRules)
    const GetBlockedContacts: Contact[] = useSelector(selector.getBlockedContacts)

    const [isContactInfo, SetopenContactInfo] = useState<boolean>(false)
    const [openEditContact, SetOpenEditContact] = useState<boolean>(false)
    const [isRulesData, SetOpenRulesData] = useState<boolean>(false)
    const [editRowId, SetEditRowId] = useState<string>("")
    const [AutoLogs, SetAutoLogs] = useState<AutoAlertLogsModel[]>([])
    const [RulesDetails, SetRulesDetails] = useState<RuleModel>(undefined)
    const [LogToEdit, SetLogToEdit] = useState<AutoAlertLogsModel>(undefined)

    useEffect(() => {
        setHeaderTitle(pageTitles.AUTOMATION_RULES_LOGS);
        setHeaderCount(AutoLogs?.length || 0)
        setRefreshHandler(refreshData);
        setSelectedCount(0)
        return setHeaderCount
    }, [AutoLogs])

    useEffect(() => {
        refreshData()
    }, [])

    const refreshData = () => {
        AutoAlertRulesService.getAutoAlertLogs(1, 1000).then(res => {
            SetAutoLogs(res.data.map(mapToAutoAlertLogs));
        }).catch(err => {
        });
    }

    const openContactInfo = (data?: AutoAlertLogsModel) => {
        SetopenContactInfo(true);
        SetLogToEdit(data);
    }

    const closeContactInfo = () => {
        SetopenContactInfo(false);
        SetLogToEdit(undefined);
    }

    const handleEditContact = () => {
        SetOpenEditContact(true);
        SetopenContactInfo(false);
    }

    const closeEditContact = () => {
        SetOpenEditContact(false);
        if (IsMobileDevice) {
            SetopenContactInfo(true);
        }
    }

    const openRulesData = (id: string, obj: AutoAlertLogsModel) => {
        let data = PrivateRules.find(x => x.ID === id)
        SetOpenRulesData(true);
        SetEditRowId(id);
        SetRulesDetails(data);
        SetLogToEdit(obj);
    }

    const closeRulesData = () => {
        SetOpenRulesData(false);
        SetEditRowId("");
        SetLogToEdit(undefined);
    };

    const ColoumnDefs: ColDef[] = [
        {
            field: "Number",
            headerName: "To",
            valueGetter: (params: ValueGetterParams) => {
                return Contacts.find(x => x.PhoneNumber === params.data.Number)?.Name
            },
            cellRenderer: (params) =>
                <span className={`color-alert-usedin c-pointer t-decoration-underline`} onClick={() => openContactInfo(params.data)}>{Contacts.find(x => x.PhoneNumber === params.data.Number)?.Name || UtilityService.maskPhoneNumberOnLength(params.data.Number)}</span>,
            minWidth: IsMobileDevice ? 70 : 200,
            cellStyle: { textOverflow: 'ellipsis', paddingTop: "15px" },
        },
        {
            field: "RuleID",
            headerName: "Rule Name",
            valueGetter: (props: ValueGetterParams) => {
                return PrivateRules.find(x => x.ID === props.data.RuleID)?.Name
            },
            cellRenderer: (props) =>
                <>
                    {PrivateRules.find(x => x.ID === props.data.RuleID)?.Name ?
                        <span className={`color-alert-usein c-pointer t-decoration-underline`} onClick={() => openRulesData(props.data.RuleID, props.data)}>{PrivateRules.find(x => x.ID === props.data.RuleID)?.Name || "--"}</span>
                        : <span>After hours message</span>
                    }
                </>,
            minWidth: IsMobileDevice ? 70 : 200
        },
        {
            field: "IsSuccess",
            headerName: "Status",
            valueGetter: (props: ValueGetterParams) => {
                return props.data.IsSuccess ? AutomationRulesLabel.COMPLETED : AutomationRulesLabel.FAILED
            },
            cellRendererSelector: params => {
                if (params.data) {
                    const label = params.data.IsSuccess ? AutomationRulesLabel.COMPLETED : AutomationRulesLabel.FAILED;
                    const variant = params.data.IsSuccess ? GridStatusLabelVariant.Active : GridStatusLabelVariant.Danger;
                    const Status = {
                        component: StatusRenderer,
                        params: {
                            Variant: variant, Label: label, Id: params.data.ID, isRules: true,
                        }
                    }
                    return Status;
                }
            },
            minWidth: IsMobileDevice ? 70 : 200
        },
        {
            field: "CreatedDateTime",
            headerName: "Time Sent",
            sort: "desc",
            valueGetter: (props: ValueGetterParams) => {
                return UtilityService.convertToDateTimeFormat(props.data.CreatedDateTime)
            },
            cellRenderer: (props) =>
                <span className={`my-auto`}>{UtilityService.convertToDateTimeFormat(props.data.CreatedDateTime)}</span>
            ,
            wrapText: true,
            autoHeight: true,
            comparator: (valueA, valueB, nodeA, nodeB) => dateComparator(nodeA.data.CreatedDateTime, nodeB.data.CreatedDateTime),
            hide: IsMobileDevice
        },
    ]

    const getGridTitle = () => {
        return AutoLogs.length === 1 ? "1 Log" : AutoLogs.length + " " + pageTitles.AUTOMATION_RULES_LOGS
    }

    return (
        isContactInfo && IsMobileDevice ? (
            <ContactInformation
                openContactInfo={isContactInfo}
                handleContactClose={closeContactInfo}
                handleEditContact={handleEditContact}
                Contact={Contacts.find(x => x.PhoneNumber === LogToEdit?.Number)
                    || {
                        PhoneNumber: LogToEdit?.Number,
                        MaskedPhoneNumber: UtilityService.maskPhoneNumberOnLength(LogToEdit?.Number),
                        IsBlocked: GetBlockedContacts.find(x => x.PhoneNumber === LogToEdit?.Number)?.IsBlocked ?? false
                    } as Contact
                }
                IsMobileDevice={IsMobileDevice}
            />
        ) :
            openEditContact && IsMobileDevice ? (
                <ContactView onClose={closeEditContact} show={openEditContact} Contact={Contacts.find(x => x.PhoneNumber === LogToEdit?.Number)
                    || {
                        PhoneNumber: LogToEdit?.Number,
                        MaskedPhoneNumber: UtilityService.maskPhoneNumberOnLength(LogToEdit?.Number),
                    } as Contact
                } />
            ) :
                <>
                    <div className='h-p-100'>
                        <GridComponent
                            ColumnDefs={ColoumnDefs}
                            RowData={AutoLogs}
                            OnGridReady={props.OnGridReady}
                            IsPaginationEnabled={!IsMobileDevice}
                            EmptyLabel={emptyLabels.LOGS}
                            LockVisible={false}
                            gridApi={props.GridApi}
                        />
                    </div>

                    {isContactInfo &&
                        <div className={`t-32 l-p-45 r-p-50 p-absolute contact-detail-view w-50`}>
                            <ContactInformation
                                openContactInfo={isContactInfo}
                                handleContactClose={closeContactInfo}
                                handleEditContact={handleEditContact}
                                Contact={Contacts.find(x => x.PhoneNumber === LogToEdit?.Number)
                                    || {
                                        PhoneNumber: LogToEdit?.Number,
                                        IsBlocked: GetBlockedContacts.find(x => x.PhoneNumber === LogToEdit?.Number)?.IsBlocked ?? false,
                                        MaskedPhoneNumber: UtilityService.maskPhoneNumberOnLength(LogToEdit?.Number),
                                    } as Contact
                                }
                                IsMobileDevice={IsMobileDevice}
                            />
                        </div>}

                    {
                        openEditContact &&
                        <ContactView onClose={closeEditContact} show={openEditContact} Contact={Contacts.find(x => x.PhoneNumber === LogToEdit?.Number)
                            || {
                                PhoneNumber: LogToEdit?.Number,
                                MaskedPhoneNumber: UtilityService.maskPhoneNumberOnLength(LogToEdit?.Number),
                            } as Contact
                        } />
                    }
                    {
                        isRulesData && (
                            <Dialog
                                fullWidth
                                maxWidth="sm"
                                onClose={closeRulesData}
                                aria-labelledby="responsive-dialog-title"
                                open={true}
                                BackdropProps={{
                                    style: { backgroundColor: "transparent" },
                                }}
                                className={"dialogue-shadow"}
                            >
                                <DialogTitle className="dialog-header" >
                                    <div className={`${IsMobileDevice ? 'h1' : 'h2-regular'} color-display-title d-flex ml-1`} id="customized-dialog-title">
                                        {PrivateRules.find(x => x.ID === editRowId).Name}
                                        <Tooltip title="Close" placement="bottom">
                                            <span className="ml-auto mr-1">
                                                <CancelIcon
                                                    className={"c-pointer"}
                                                    onClick={closeRulesData}
                                                />
                                            </span>
                                        </Tooltip>
                                    </div>
                                </DialogTitle>
                                <DialogContent className={"o-hidden"}>
                                    <div className="br-1 settings-paper-border mb-2">
                                        <Grid container direction="row" spacing={2} className="p-2">
                                            <Grid item xs={12} md={12} lg={12} className="mt-1">
                                                <span className="h5-regular  color-text-field-label">
                                                    Name
                                                </span>
                                                <br />
                                                <span className="h4-regular color-display-title">
                                                    {RulesDetails.Name}
                                                </span>
                                            </Grid>

                                            <Grid item xs={6} md={6} lg={6} className="mt-1">
                                                <span className="h5-regular color-text-field-label">
                                                    Type
                                                </span>
                                                <br />
                                                <span className="h4-regular color-display-title">
                                                    {RulesDetails.Type === RuleType.AUTO_REPLY ? AutomationRulesLabel.AUTO_REPLY : AutomationRulesLabel.KEYWORD}
                                                </span>
                                            </Grid>
                                            <Grid item xs={6} md={6} lg={6} className="mt-1">
                                                <span className="h5-regular  color-text-field-label">
                                                    Keyword
                                                </span>
                                                <br />
                                                <span className="h4-regular color-display-title">

                                                    {UtilityService.trimMessage(LogToEdit?.Keyword, 25) || "--"}
                                                </span>
                                            </Grid>
                                            <Grid item xs={12} md={12} lg={12} className="mt-1  ">
                                                <span className="h5-regular  color-text-field-label">
                                                    Message
                                                </span>
                                                <br />
                                                <div className="h4-regular color-display-title max-h-60 o-y-scroll d-flex align-center justify-between">
                                                    <span className='max-h-60 o-y-scroll pr-1'>
                                                        <Mention body={RulesDetails.Message} tags={Tags} emoji={''} openTags={false} tagTarget={undefined} onChange={() => { }} onKeyDown={() => { }} closeTags={() => { }} maxLength={RulesDetails.Message.length} id={''} placeholder={''} readOnly={true} IsLinkAllowed={true} IsOutbound={true} />
                                                    </span>
                                                    {!!RulesDetails.Media?.length && <span className='w-p-20 d-flex mx-auto ml-1'><MediaDefaultIcon /></span>}
                                                </div>
                                            </Grid>
                                        </Grid>
                                    </div>
                                </DialogContent>
                            </Dialog>
                        )
                    }
                </>
    )

}

export default LogsComponent